<template>
  <div>
    <span v-if="minQtyErrorPresent" v-html="bp('skuGridErrorMessage')"></span>

    <table class="b2b-grid b2b-grid--collapse is-compact" qaid="saved-list-sku-grid">
      <!-- SKU Grid Headers -->
      <thead class="b2b-grid__header">
        <tr>
          <th class="b2b-grid__cell b2b-grid__head w-4">
            <span :qaid="`sku-grid-checkbox-${baseProduct.baseProduct}`" @click.prevent.stop="checkAll">
              <grid-checkbox :label="baseProduct.baseProduct" :checked="isAllChecked" />
            </span>
          </th>
          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.icon"></th>

          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.note"></th>

          <th class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.sku" v-html="bp('sku')"></th>

          <th v-if="isProjectOne" class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.item" v-html="bp('item')"></th>
          <th v-else class="b2b-grid__cell b2b-grid__head uppercase" qaid="sku-grid.upc-ean" v-html="bp('upc')"></th>

          <th
            v-if="isCustomWaterBottle || isProjectOne"
            class="b2b-grid__cell b2b-grid__head lg:text-right"
            qaid="sku-grid-custom-id"
          >
            <span v-html="bp('customId')" />
          </th>
          <template v-else>
            <th class="b2b-grid__cell b2b-grid__head lg:text-right" qaid="sku-grid.allocated">
              <span
                v-tippy="{
                  html: '#abbreviation-text',
                  trigger: 'mouseenter',
                }"
                v-html="bp('alloc')"
              >
              </span>
            </th>

            <th class="b2b-grid__cell b2b-grid__head lg:text-right" qaid="sku-grid.backordered">
              <span
                v-tippy="{
                  html: '#bo-abbreviation-text',
                  trigger: 'mouseenter',
                }"
                v-html="bp('bo')"
              >
              </span>
            </th>
          </template>

          <!-- Variable variant headers -->
          <template v-for="(variant, variantIndex) in variantHeaders">
            <th :key="variantIndex" class="b2b-grid__cell b2b-grid__head">
              <span v-if="variant.name === 'Size'" qaid="sku-grid.size" v-html="bp('size')"></span>

              <span v-else-if="variant.name === 'Color'" v-html="bp('color')"></span>

              <span v-else>
                <!-- Unknown variant - Printing the raw name -->
                {{ variant.name }}
              </span>
            </th>
          </template>

          <th
            v-if="!consumerFriendlyMode && !isProjectOne"
            class="b2b-grid__cell b2b-grid__head"
            qaid="sku-grid.order-price"
            v-html="bp('price')"
          ></th>

          <th v-if="!isProjectOne" qaid="sku-grid.list" class="b2b-grid__cell b2b-grid__head lg:text-right">
            <i class="md-18">list</i>
          </th>

          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid.package-quantity" v-html="bp('qty')"></th>

          <th
            v-if="!consumerFriendlyMode"
            class="b2b-grid__cell b2b-grid__head text-right"
            qaid="sku-grid.subtotal"
            v-html="bp('subtotal')"
          ></th>

          <th class="b2b-grid__cell b2b-grid__head w-5" qaid="sku-grid.trash"></th>
        </tr>
      </thead>
      <span id="abbreviation-text" class="hidden" v-html="bp('allocated')"></span>
      <span id="bo-abbreviation-text" class="hidden" v-html="bp('backordered')"> </span>
      <!-- SKU Grid Body/Rows -->
      <tbody>
        <!-- For each Product/Row -->
        <cart-product-grid-row
          v-for="(product, productIndex) in sortedProducts"
          :key="`${productIndex}-${product.cartEntryPk}`"
          :base-product="baseProduct"
          :base-product-index="baseProductIndex"
          :base-properties="baseProperties"
          :product="product"
          :min-qty-error="minQtyError(product)"
          :min-qty-error-msg="minQtyErrorMessage(product)"
          :row-index="productIndex"
          :is-custom-water-bottle="isCustomWaterBottle"
          :is-project-one="isProjectOne"
          @open-confirm-delete-dialog="openConfirmDeleteDialog"
          @send-cart-update="sendCartUpdate"
        />
      </tbody>
    </table>

    <el-dialog
      :visible.sync="dialogVisible"
      :title="bp('confirm')"
      :center="true"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :z-index="16000011"
      :close-dialog-tooltip="bp('closeDialogToolTip')"
      @close="resetDeleteData"
    >
      <template slot="footer">
        <div class="buttons buttons--center">
          <trek-button alert qaid="confirm-delete-button" @click="confirmDelete">
            <span v-html="bp('remove')"></span>
          </trek-button>
          <trek-button secondary qaid="not-remove-button" @click="dialogVisible = false">
            <span v-html="bp('doNotRemove')"></span>
          </trek-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import {mapActions, mapMutations, mapGetters} from 'vuex';
import TrekButton from '@/components/TrekButton';
import ElDialog from '../Dialog.vue';
import GridCheckbox from '../grid/GridCheckbox';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import CartProductGridRow from '@/components/cart/CartProductGridRow';
import {SortedProducts} from '@/components/containers/bulk-product/mixins/sorted-products';

export default {
  components: {TrekButton, ElDialog, GridCheckbox, CartProductGridRow},

  mixins: [SortedProducts],
  props: {
    cartId: {
      type: String,
      default: '',
    },
    baseProperties: {
      type: Array,
      default: () => [],
    },
    baseProduct: {
      type: Object,
      default: () => {},
    },
    baseProductIndex: {
      type: Number,
      default: 0,
    },
    partType: {
      type: String,
      default: '',
    },
    isCustomWaterBottle: {
      type: Boolean,
      default: false,
    },
    isProjectOne: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialogVisible: false,
      itemToDeleteElement: null,
      itemDeleted: false,
      minQtyFailList: {},
      minQtyErrorPresent: false,
      isAllChecked: false,
    };
  },

  computed: {
    sortedProducts() {
      return this.getSortedProducts(this.baseProduct.entries);
    },

    variantHeaders() {
      const variantItem = this.baseProduct;
      const categoriesObject = get(variantItem, 'entries[0].variantValueCategories[0]');

      if (categoriesObject) {
        const categories = Object.keys(categoriesObject);
        let variants = [];

        categories.forEach(function(category) {
          if (variantItem.groupBy != category) {
            variants.push({name: category});
          }
        });

        return variants;
      }

      return null;
    },

    ...mapGetters('backend', ['consumerFriendlyMode']),
    ...mapGetters('cart', ['selectedProducts']),
  },

  watch: {
    selectedProducts() {
      this.isAllChecked = this.isEveryEntrySelected();
    },
  },

  created() {
    if (this.baseProduct?.entries[0]?.p1Flag === 'P1BIKE') {
      this.baseProduct.entries[0].itemName = this.baseProduct.itemName;
    }
  },

  mounted() {
    this.isAllChecked = this.isEveryEntrySelected();
  },

  methods: {
    checkAll() {
      this.isAllChecked = !this.isAllChecked;
      if (this.isAllChecked) {
        this.baseProduct.entries.forEach((entry) => {
          this.addSelectedProduct({
            sku: entry.sku,
            qty: this.getProductQuantity(entry),
            productType: entry.productType,
            lineNote: entry.lineNote,
            productRecipeID: entry.productRecipeID,
            cartEntryPk: entry.cartEntryPk,
          });
        });
      } else {
        this.baseProduct.entries.forEach((entry) => {
          this.removeSelectedProduct(entry.cartEntryPk);
        });
      }
    },

    getProductQuantity(product) {
      return reduce(product.warehouses, (result, warehouse) => result + parseInt(warehouse.orderedQty), 0);
    },

    isEveryEntrySelected() {
      return this.baseProduct.entries.every(
        (entry) => !!this.selectedProducts.find((product) => entry.cartEntryPk === product.cartEntryPk)
      );
    },

    openConfirmDeleteDialog(element) {
      this.dialogVisible = true;
      this.itemToDeleteElement = element;
    },

    // This is called when the confirmation dialog closes
    resetDeleteData() {
      this.dialogVisible = false;

      if (!this.itemDeleted) {
        this.itemToDeleteElement.value = this.itemToDeleteElement.dataset.initialValue;
      }

      this.itemDeleted = false;
      this.itemToDeleteElement = null;
    },

    confirmDelete() {
      this.dialogVisible = false;
      this.itemDeleted = true;
      const sku = this.itemToDeleteElement.dataset.sku;
      const cartEntryPk = this.itemToDeleteElement.dataset.pk;
      const productRecipeID = this.itemToDeleteElement.dataset.productRecipeId;

      this.minQtyErrorPresent = false;
      this.minQtyFailList = {};

      this.updateCart([{sku: sku, qty: 0, productRecipeID: productRecipeID, cartEntryPk: cartEntryPk}], true)
        .then((response) => {
          let messages = (get(response, 'data.data.failedSKUs') || []).map((item) => item.message.basePropertyValue);
          let cartEntryPks = (get(response, 'data.data.failedSKUs') || []).map((item) => item.cartEntryPk);

          this.updateFailedPks(messages, cartEntryPks);
        })
        .finally(() => {
          this.fetchCart().then(() => {
            this.removeSelectedProduct(cartEntryPk);
            this.isAllChecked = this.isEveryEntrySelected();
          });
          this.fetchMiniCart();
        });
    },

    updateFailedPks(messages, cartEntryPks) {
      this.minQtyFailList = {};

      if (cartEntryPks.length) this.minQtyErrorPresent = true;

      for (let i = 0; i < cartEntryPks.length; i++) {
        Vue.set(this.minQtyFailList, cartEntryPks[i], messages[i]);
      }
    },

    sendCartUpdate(cartData) {
      this.minQtyErrorPresent = false;
      this.minQtyFailList = {};

      this.updateCart([...cartData]).then((response) => {
        let messages = (get(response, 'data.data.failedSKUs') || []).map((item) => item.message.basePropertyValue);
        let cartEntryPks = (get(response, 'data.data.failedSKUs') || []).map((item) => item.cartEntryPk);

        this.updateFailedPks(messages, cartEntryPks);
      });
    },

    minQtyError(product) {
      return !!(this.minQtyFailList && this.minQtyFailList[product.cartEntryPk]);
    },

    minQtyErrorMessage(product) {
      return this.minQtyFailList[product.cartEntryPk];
    },

    ...mapMutations('savedLists', ['setEntries']),

    ...mapActions('cart', [
      'removeProducts',
      'undoRemove',
      'updateCart',
      'fetchCart',
      'removeSelectedProduct',
      'addSelectedProduct',
    ]),

    ...mapActions('miniCart', ['fetchMiniCart']),
  },
};
</script>
